// Office nav lucide icons (Session AGZ, Director decision superseding the AFA two-letter
// glyph system). One icon per route, keyed by the nav item's `to` path -- a RENDERING
// lookup only. NavItem.icon (the two-letter glyph string) stays on the pure nav data:
// nav.test.ts pins it as a non-empty string, applyNavLayout round-trips it, and it remains
// the fallback for any future nav item added without a mapping here (the chip renders the
// glyph rather than nothing, so a missing mapping is visible, never a blank tile).
//
// Icon choice follows the platform's lucide conventions (ThemeToggle, the engineer PWA):
// components render via currentColor at a fixed size, coloured by the parent's classes,
// so the locked ACG green-active / purple-hover nav treatment carries over untouched.
import type { ComponentType } from "react";
import {
  AlertTriangle,
  BriefcaseMedical,
  BellRing,
  BarChart2,
  BookOpen,
  BookOpenCheck,
  Bot,
  Brain,
  Building2,
  Calendar,
  CalendarClock,
  ClipboardCheck,
  Clock4,
  Eye,
  FileCheck,
  FileClock,
  FileText,
  LayoutTemplate,
  Files,
  FolderKanban,
  Gauge,
  Gavel,
  GraduationCap,
  Handshake,
  HardHat,
  Inbox,
  KeyRound,
  Layers,
  LayoutDashboard,
  Leaf,
  Library,
  ListChecks,
  Map,
  MapPin,
  MessageSquareWarning,
  MessagesSquare,
  MoveVertical,
  Package,
  PhoneCall,
  Receipt,
  RefreshCw,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Timer,
  TrendingDown,
  TrendingUp,
  UserCheck,
  UserCog,
  UserSearch,
  History,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";

export type NavIconComponent = ComponentType<{ size?: number | string; className?: string; "aria-hidden"?: boolean }>;

const NAV_ICONS: Record<string, NavIconComponent> = {
  // Overview
  "/office": LayoutDashboard,
  "/office/nps": Gauge,
  // Portfolio
  "/office/customers": Users,
  "/office/sites": Building2,
  "/office/lifts": MoveVertical,
  "/office/map": Map,
  "/office/contracts": FileCheck,
  // Maintenance
  "/office/scheduling": Calendar,
  "/office/ppm": CalendarClock,
  "/office/defects": AlertTriangle,
  "/office/renewals": RefreshCw,
  // Field work
  "/office/jobsheets": Wrench,
  "/office/callouts": PhoneCall,
  "/office/standby": BellRing, // Session ANY: who is on call this week
  "/office/leave": Calendar, // Session ASZ (D-V1): who is away when
  "/office/expenses": Wallet, // Session ATE: mileage/materials/other engineer expense claims
  "/office/lone-worker": ShieldAlert, // Session AOB: who is checked in on site right now
  "/office/mail": Inbox, // Session AUB: the Mail workspace (replaced inbox triage, enquiries, the email engine and work orders)
  "/office/messages": MessagesSquare, // restored 28 Sep 2026: one-to-one messages with each engineer
  "/office/reply-templates": LayoutTemplate, // Session AUA: the approved wording the email engine fills
  // Operations
  "/office/projects": FolderKanban,
  "/office/subcontractors": Handshake,
  // Compliance
  "/office/compliance": ShieldCheck,
  "/office/compliance/documents": Library,
  "/office/company-documents": Files,
  "/office/loler": ClipboardCheck,
  "/office/loler-due": ClipboardCheck, // Session SC7A: the LOLER and SAFed hub opens on Due dates
  "/office/safed": Gauge, // Session BDC: the SAFed tests worklist
  "/office/complaints": MessageSquareWarning,
  // Session AOM: the accident and incident register. BriefcaseMedical rather than the
  // AlertTriangle already used for Defects, so the two are not confusable in the collapsed rail:
  // one is a fault on a lift, the other is somebody hurt.
  "/office/accidents": BriefcaseMedical,
  // Session AON: the enforcement notices and prosecutions register. Gavel reads unambiguously as a
  // legal/enforcement action, distinct from both BriefcaseMedical (an injury) and AlertTriangle (a
  // lift fault) in the collapsed rail.
  "/office/enforcement-actions": Gavel,
  "/office/compliance/audits": ListChecks,
  "/office/compliance/management-reviews": BookOpenCheck,
  "/office/environmental-records": Leaf,
  // Session ALU: ONE entry for the merged Personnel page (Office / Engineer tabs). The two it
  // replaces, "/office/personnel-compliance" (UserCheck) and "/office/engineer-compliance"
  // (BadgeCheck), are no longer nav items, so their mappings are gone with them -- both paths
  // still RESOLVE, as redirects to the tab they became, but a redirect never renders a nav chip.
  "/office/personnel": UserCheck,
  "/office/compliance-reports": ScrollText,
  "/office/auditors": Eye,
  // Training
  "/office/training": GraduationCap,
  "/office/training-documents": BookOpen,
  // Knowledge
  "/office/knowledge": Brain,
  // Finance
  "/office/quotes": FileText,
  "/office/quote-analytics": TrendingUp,
  "/office/quote-portfolios": Layers,
  // Session ATB (Phase 4, D30, M-5): Invoices/Aged debtors/PPM payments/Allowances folded into the
  // Billing hub as a single nav item; their own icon mappings are dead entries in this map now
  // (harmless, but replaced by the one hub entry so the map does not silently grow stale ones).
  "/office/billing": Receipt,
  // Session ASY (merged the same day): Purchase orders had no icon mapping, found and fixed here
  // while resolving this session's own merge with it.
  "/office/purchase-orders": Package,
  "/office/reporting": BarChart2,
  "/office/sla-report": Timer,
  "/office/lift-performance": TrendingDown,
  // Admin
  "/office/office-users": UserCog,
  "/office/engineers": HardHat,
  "/office/timesheets": Clock4,
  "/office/portal-users": KeyRound,
  "/office/coverage-zones": MapPin,
  "/office/connected-ai": Bot,
  "/office/personal-data": UserSearch,
  "/office/data-retention": FileClock,
  "/office/activity": History,
  "/office/settings": Settings,
};

// The rendering lookup. Null for an unmapped route (the chip falls back to the glyph).
export function navIconFor(to: string): NavIconComponent | null {
  return NAV_ICONS[to] ?? null;
}
